import { useCallback, useEffect, useState } from "react";
import { Search, Trash2, Mail, MailOpen, X, Loader2, Building2, Eye } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../adminApi";

function Detail({ item, type, onClose }) {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="submission-detail">
      <div className="bg-white rounded-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-lg font-bold text-[#0A2342]">
            {type === "contact" ? item.name : item.company}
          </h3>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#0A2342]"><X size={20} /></button>
        </div>
        <div className="mt-4 space-y-2 text-sm">
          {type === "contact" ? (
            <>
              <Row label="Email" value={item.email} />
              <Row label="Phone" value={item.phone || "—"} />
              <Row label="Company / Agency" value={item.company || "—"} />
              <Row label="Subject" value={item.subject || "—"} />
              <Row label="Message" value={item.message} />
            </>
          ) : (
            <>
              <Row label="Email" value={item.email} />
              <Row label="Phone" value={item.phone || "—"} />
              <Row label="Website" value={item.website || "—"} />
              <Row label="Products / Services" value={item.products} />
              <Row label="Message" value={item.message || "—"} />
            </>
          )}
          <Row label="Received" value={new Date(item.created_at).toLocaleString()} />
        </div>
      </div>
    </div>
  );
}
const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 border-b border-[#F4F6F8] py-2">
    <span className="text-[#4B5563]">{label}</span>
    <span className="col-span-2 text-[#0A2342] break-words">{value}</span>
  </div>
);

export default function AdminSubmissions() {
  const [tab, setTab] = useState("contact");
  const [contacts, setContacts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v] = await Promise.all([
        adminApi.get("/admin/contacts", { params: { search } }),
        adminApi.get("/admin/vendors", { params: { search } }),
      ]);
      setContacts(c.data);
      setVendors(v.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const markRead = async (item) => {
    await adminApi.patch(`/admin/contacts/${item.id}/read`);
    setContacts((cs) => cs.map((c) => (c.id === item.id ? { ...c, read: true } : c)));
  };
  const openContact = (item) => {
    setSelected({ item, type: "contact" });
    if (!item.read) markRead(item);
  };
  const del = async (type, id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    await adminApi.delete(`/admin/${type === "contact" ? "contacts" : "vendors"}/${id}`);
    toast.success("Deleted");
    if (type === "contact") setContacts((cs) => cs.filter((c) => c.id !== id));
    else setVendors((vs) => vs.filter((v) => v.id !== id));
  };

  const list = tab === "contact" ? contacts : vendors;

  return (
    <div className="space-y-6" data-testid="admin-submissions">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0A2342]">Submissions</h1>
        <p className="text-sm text-[#4B5563] mt-1">Contact enquiries and vendor partnership applications.</p>
      </div>

      <div className="flex items-center gap-2">
        <button data-testid="tab-contacts" onClick={() => setTab("contact")} className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "contact" ? "bg-[#0A2342] text-white" : "bg-white border border-[#E5E7EB] text-[#4B5563]"}`}>
          Contacts ({contacts.length})
        </button>
        <button data-testid="tab-vendors" onClick={() => setTab("vendor")} className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "vendor" ? "bg-[#0A2342] text-white" : "bg-white border border-[#E5E7EB] text-[#4B5563]"}`}>
          Vendors ({vendors.length})
        </button>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
        <input data-testid="submissions-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-full max-w-md rounded-lg border border-[#E5E7EB] pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#1E88E5]" />
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-[#4B5563]"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-[#4B5563] text-sm">No submissions.</div>
        ) : (
          <ul className="divide-y divide-[#F4F6F8]">
            {list.map((item) => (
              <li key={item.id} className={`flex items-center gap-4 px-5 py-4 ${tab === "contact" && !item.read ? "bg-[#1E88E5]/[0.04]" : ""}`} data-testid={`submission-row-${item.id}`}>
                <span className="h-10 w-10 rounded-lg bg-[#F4F6F8] flex items-center justify-center text-[#1E88E5] shrink-0">
                  {tab === "contact" ? (item.read ? <MailOpen size={18} /> : <Mail size={18} />) : <Building2 size={18} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#0A2342] truncate">{tab === "contact" ? item.name : item.company}</p>
                    {tab === "contact" && !item.read && <span className="text-[10px] font-bold bg-[#1E88E5] text-white rounded-full px-2 py-0.5">NEW</span>}
                  </div>
                  <p className="text-sm text-[#4B5563] truncate">{item.email} • {tab === "contact" ? (item.subject || "Inquiry") : item.products}</p>
                </div>
                <span className="hidden sm:block text-xs text-[#4B5563] whitespace-nowrap">{new Date(item.created_at).toLocaleDateString()}</span>
                <button onClick={() => (tab === "contact" ? openContact(item) : setSelected({ item, type: "vendor" }))} data-testid={`view-${item.id}`} className="p-2 text-[#4B5563] hover:text-[#1E88E5]" title="View"><Eye size={18} /></button>
                <button onClick={() => del(tab, item.id)} data-testid={`delete-${item.id}`} className="p-2 text-[#4B5563] hover:text-red-600" title="Delete"><Trash2 size={18} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && <Detail item={selected.item} type={selected.type} onClose={() => setSelected(null)} />}
    </div>
  );
}
